import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";

// Translation resources
const resources = {
  en: {
    translation: {
      tabs: {
        home: "Home",
        location: "Location",
        settings: "Settings",
      },
      network: {
        status: "Network Status",
        connected: "Connected to Internet",
        disconnected: "No Internet Connection",
      },
      api: {
        test: "API Testing",
        testButton: "Test API Call",
        success: "API call successful!",
        error: "API call failed. Please try again.",
      },
      location: {
        permissions: "Location Permissions",
        requestPermissions: "Request Location Permissions",
        current: "Current Location",
        getCurrentLocation: "Get Current Location",
        details: "Location Details",
        latitude: "Latitude",
        longitude: "Longitude",
        accuracy: "Accuracy",
        altitude: "Altitude",
        meters: "meters",
        success: "Location retrieved successfully!",
        error: "Failed to get location. Please try again.",
        permissionGranted: "Location permission granted!",
        permissionDenied: "Location permission denied.",
        permissionError: "Error requesting location permission.",
      },
      settings: {
        language: "Language Settings",
        selectLanguage: "Select Language",
        userInfo: "User Information",
        name: "Name",
        phone: "Phone Number",
        email: "Email",
        userId: "User ID",
        lastLogin: "Last Login",
        appInfo: "App Information",
        version: "Version",
        platform: "Platform",
        account: "Account",
        logout: "Logout",
        confirmLogout: "Confirm Logout",
        logoutMessage: "Are you sure you want to logout?",
      },
      languages: {
        english: "English",
        marathi: "मराठी",
      },
      common: {
        loading: "Loading...",
        error: "An error occurred",
        success: "Success",
        cancel: "Cancel",
        ok: "OK",
      },
      reports: {
        dashboard: "Report Status Dashboard",
        searchPlaceholder: "Search reports...",
        allReports: "All Reports",
        raised: "Raised",
        pending: "Raised",
        acknowledged: "Acknowledged",
        in_progress: "In Progress",
        resolved: "Done",
        viewDetails: "View Details",
        submitReport: "Submit New Report",
        submitted: "Submitted",
        hoursAgo: "hours ago",
        dayAgo: "day ago",
        daysAgo: "days ago",
        weekAgo: "week ago",
        weeksAgo: "weeks ago",
        urgentAttention: "Urgent attention needed",
        noReportsFound: "No reports found",
        loadingReports: "Loading reports...",
        reportSubmitted: "Report submitted successfully",
        failedToLoad: "Failed to load reports",
        errors: {
          unauthorized: "Session expired. Please login again.",
          forbidden: "You don't have permission to view these reports.",
          serverError: "Server error occurred. Please try again later.",
          networkError:
            "Network connection failed. Please check your internet connection.",
        },
      },
      submitReport: {
        title: "Submit New Report",
        sections: {
          reportDetails: "Report Details",
          images: "Images",
          location: "Location",
          userDetails: "User Details",
        },
        fields: {
          title: "Title",
          issueType: "Issue Type",
          priority: "Priority",
          description: "Description",
          name: "Full Name",
          phone: "Phone Number",
          email: "Email Address",
        },
        placeholders: {
          title: "Enter report title",
          issueType: "Select issue type",
          description: "Please describe the issue in detail...",
          name: "Enter your full name",
          phone: "Enter your phone number",
          email: "Enter your email address",
        },
        priority: {
          low: "Low",
          medium: "Medium",
          high: "High",
          urgent: "Urgent",
        },
        buttons: {
          addImage: "Add Image",
          getCurrentLocation: "Get Current Location",
          gettingLocation: "Getting Location...",
          submit: "Submit Report",
          submitting: "Submitting...",
          cancel: "Cancel",
          maxImagesReached: "Maximum images reached",
        },
        imageOptions: {
          camera: "Take Photo",
          gallery: "Choose from Gallery",
        },
        validation: {
          titleRequired: "Report title is required",
          issueTypeRequired: "Issue type is required",
          descriptionRequired: "Description is required",
          imageRequired: "At least one image is required",
          nameRequired: "Full name is required",
          phoneRequired: "Phone number is required",
          emailRequired: "Email address is required",
          emailInvalid: "Please enter a valid email address",
        },
        errors: {
          photoFailed: "Failed to capture/select photo",
          locationFailed: "Failed to get current location",
          validationFailed: "Please fix the validation errors",
          submitFailed: "Failed to submit report. Please try again.",
          maxImagesReached: "Maximum {{max}} images allowed",
          tooManyImages:
            "You can only add {{remaining}} more image(s). Maximum {{max}} images allowed.",
        },
        success: {
          locationCaptured: "Location captured successfully",
          submitted: "Report submitted successfully!",
        },
        loading: {
          submitting: "Submitting your report...",
        },
        locationCaptured: "Location captured",
        name: "Name",
        namePlaceholder: "Enter report name or title",
        issueType: "Type of Issue",
        issueTypePlaceholder: "Select issue type",
        description: "Description",
        descriptionPlaceholder: "Please describe the issue in detail...",
        images: "Images",
        dragFiles: "Drag files here or click to upload",
        supportedFormats: "Supports: JPG, PNG, GIF (Max 5MB each)",
        takePhoto: "Take Photo",
        chooseFromGallery: "Choose from Gallery",
        cancel: "Cancel",
        submitButton: "Submit Report",
        submitting: "Submitting report...",
        submitSuccess: "Report submitted successfully!",
        submitError: "Failed to submit report. Please try again.",
        titleRequired: "Report title is required",
        issueTypeRequired: "Issue type is required",
        descriptionRequired: "Description is required",
        photoError: "Error accessing camera/gallery",
        locationError: "Could not get your location",
      },
      issueTypes: {
        pothole: "Pothole",
        streetlight: "Street Light",
        drainage: "Drainage",
        waste: "Waste Management",
        traffic: "Traffic",
        accident: "Traffic Accident",
        roadwork: "Road Construction/Work",
        flooding: "Flooding",
        "illegal-dumping": "Illegal Dumping",
        damagedroad: "Damaged Road Surface",
        plumbing: "Plumbing",
        electrical: "Electrical",
        hvac: "HVAC/Heating",
        security: "Security",
        maintenance: "Maintenance",
        cleaning: "Cleaning",
        it: "IT/Technology",
        other: "Other",
      },
      login: {
        welcomeBack: "Welcome Back",
        signInToAccount: "Sign in to your account",
        emailOrMobile: "Email or Mobile Number",
        enterEmailOrMobile: "Enter your email or mobile number",
        password: "Password",
        enterPassword: "Enter your password",
        rememberMe: "Remember me",
        forgotPassword: {
          link: "Forgot password?",
          sent: "Password reset link sent to your email or mobile",
        },
        signIn: "Sign In",
        signingIn: "Signing in...",
        noAccount: "Don't have an account?",
        signUp: {
          link: "Sign up",
          redirect: "Redirecting to sign up...",
        },
        orContinueWith: "Or continue with",
        socialLogin: "Social login with",
        success: "Login successful! Welcome back.",
        errors: {
          general: "Login failed. Please try again.",
          invalidCredentials:
            "Invalid email/mobile or password. Please check your credentials.",
          userNotFound: "No account found with this email or mobile number.",
          accountLocked:
            "Your account has been temporarily locked. Please try again later.",
          tooManyAttempts:
            "Too many login attempts. Please try again after some time.",
          serverError: "Server error occurred. Please try again later.",
          networkError:
            "Network connection failed. Please check your internet connection.",
        },
        validation: {
          required: "Email/Mobile and password are required",
          invalidEmailOrMobile:
            "Please enter a valid email address or mobile number",
        },
      },
      signup: {
        title: "Create Account",
        subtitle: "Join us today",
        firstName: "First Name",
        firstNamePlaceholder: "First name",
        lastName: "Last Name",
        lastNamePlaceholder: "Last name",
        emailOrMobile: "Email or Mobile Number",
        emailOrMobilePlaceholder: "Enter your email or mobile number",
        password: "Password",
        passwordPlaceholder: "Create a password",
        confirmPassword: "Confirm Password",
        confirmPasswordPlaceholder: "Confirm your password",
        agreeToTerms: "I agree to the",
        termsOfService: "Terms of Service",
        and: "and",
        privacyPolicy: "Privacy Policy",
        createAccount: "Create Account",
        creating: "Creating account...",
        success: "Account created successfully!",
        error: "Failed to create account. Please try again.",
        errors: {
          userExists:
            "An account with this email or mobile number already exists",
          invalidData: "Please check your information and try again",
          serverError: "Server error occurred. Please try again later",
          networkError:
            "Network connection failed. Please check your internet connection",
        },
        haveAccount: "Already have an account?",
        signIn: "Sign in",
        passwordStrength: {
          strength: "Password strength",
          weak: "Weak",
          fair: "Fair",
          good: "Good",
          strong: "Strong",
        },
        validation: {
          firstName: "First name is required",
          lastName: "Last name is required",
          emailOrMobile: "Please enter a valid email address or mobile number",
          password: "Password must be at least 6 characters",
          confirmPassword: "Passwords do not match",
          terms: "Please agree to the terms and conditions",
        },
      },
    },
  },
  mr: {
    translation: {
      tabs: {
        home: "मुख्यपृष्ठ",
        location: "स्थान",
        settings: "सेटिंग्ज",
      },
      network: {
        status: "नेटवर्क स्थिती",
        connected: "इंटरनेटशी जोडले आहे",
        disconnected: "इंटरनेट कनेक्शन नाही",
      },
      api: {
        test: "API चाचणी",
        testButton: "API कॉल चाचवा",
        success: "API कॉल यशस्वी!",
        error: "API कॉल अयशस्वी. कृपया पुन्हा प्रयत्न करा.",
      },
      location: {
        permissions: "स्थान परवानग्या",
        requestPermissions: "स्थान परवानग्या विनंती करा",
        current: "सध्याचे स्थान",
        getCurrentLocation: "सध्याचे स्थान मिळवा",
        details: "स्थान तपशील",
        latitude: "अक्षांश",
        longitude: "रेखांश",
        accuracy: "अचूकता",
        altitude: "उंची",
        meters: "मीटर",
        success: "स्थान यशस्वीरित्या मिळवले!",
        error: "स्थान मिळविण्यात अयशस्वी. कृपया पुन्हा प्रयत्न करा.",
        permissionGranted: "स्थान परवानगी दिली!",
        permissionDenied: "स्थान परवानगी नाकारली.",
        permissionError: "स्थान परवानगी विनंती करताना त्रुटी.",
      },
      settings: {
        language: "भाषा सेटिंग्ज",
        selectLanguage: "भाषा निवडा",
        userInfo: "वापरकर्ता माहिती",
        name: "नाव",
        phone: "फोन नंबर",
        email: "ईमेल",
        userId: "वापरकर्ता आयडी",
        lastLogin: "शेवटचे लॉगिन",
        appInfo: "अॅप माहिती",
        version: "आवृत्ती",
        platform: "प्लॅटफॉर्म",
        account: "खाते",
        logout: "लॉगआउट",
        confirmLogout: "लॉगआउट करण्याची पुष्टी करा",
        logoutMessage: "तुम्हाला खरोखर लॉगआउट करायचे आहे का?",
      },
      languages: {
        english: "English",
        marathi: "मराठी",
      },
      common: {
        loading: "लोड होत आहे...",
        error: "एक त्रुटी आली",
        success: "यश",
        cancel: "रद्द करा",
        ok: "ठीक आहे",
      },
      reports: {
        dashboard: "अहवाल स्थिती डॅशबोर्ड",
        searchPlaceholder: "अहवाल शोधा...",
        allReports: "सर्व अहवाल",
        raised: "उपस्थित केले",
        pending: "उपस्थित केले",
        acknowledged: "मान्यता दिली",
        in_progress: "प्रगतीत आहे",
        resolved: "पूर्ण झाले",
        viewDetails: "तपशील पहा",
        submitReport: "नवीन अहवाल सादर करा",
        submitted: "सादर केले",
        hoursAgo: "तासांपूर्वी",
        dayAgo: "दिवसापूर्वी",
        daysAgo: "दिवसांपूर्वी",
        weekAgo: "आठवड्यापूर्वी",
        weeksAgo: "आठवड्यांपूर्वी",
        urgentAttention: "तातडीचे लक्ष आवश्यक",
        noReportsFound: "कोणते अहवाल सापडले नाहीत",
        loadingReports: "अहवाल लोड होत आहेत...",
        reportSubmitted: "अहवाल यशस्वीरित्या सादर केला",
        failedToLoad: "अहवाल लोड करण्यात अयशस्वी",
        errors: {
          unauthorized: "सेशन संपले आहे. कृपया पुन्हा लॉगिन करा.",
          forbidden: "तुम्हाला हे अहवाल पाहण्याची परवानगी नाही.",
          serverError: "सर्व्हर त्रुटी आली आहे. कृपया नंतर प्रयत्न करा.",
          networkError:
            "नेटवर्क कनेक्शन अयशस्वी. कृपया आपले इंटरनेट कनेक्शन तपासा.",
        },
      },
      submitReport: {
        title: "नवीन अहवाल सादर करा",
        sections: {
          reportDetails: "अहवाल तपशील",
          images: "छायाचित्रे",
          location: "स्थान",
          userDetails: "वापरकर्ता तपशील",
        },
        fields: {
          title: "शीर्षक",
          issueType: "समस्येचा प्रकार",
          priority: "प्राधान्य",
          description: "वर्णन",
          name: "पूर्ण नाव",
          phone: "फोन नंबर",
          email: "ईमेल पत्ता",
        },
        placeholders: {
          title: "अहवालाचे शीर्षक टाका",
          issueType: "समस्येचा प्रकार निवडा",
          description: "कृपया समस्येचे तपशीलवार वर्णन करा...",
          name: "आपले पूर्ण नाव टाका",
          phone: "आपला फोन नंबर टाका",
          email: "आपला ईमेल पत्ता टाका",
        },
        priority: {
          low: "कमी",
          medium: "मध्यम",
          high: "उच्च",
          urgent: "तातडीचे",
        },
        buttons: {
          addImage: "छायाचित्र जोडा",
          getCurrentLocation: "सध्याचे स्थान मिळवा",
          gettingLocation: "स्थान मिळवत आहे...",
          submit: "अहवाल सादर करा",
          submitting: "सादर करत आहे...",
          cancel: "रद्द करा",
          maxImagesReached: "जास्तीत जास्त छायाचित्रे पोहोचली",
        },
        imageOptions: {
          camera: "फोटो घ्या",
          gallery: "गॅलरीमधून निवडा",
        },
        validation: {
          titleRequired: "अहवालाचे शीर्षक आवश्यक आहे",
          issueTypeRequired: "समस्येचा प्रकार आवश्यक आहे",
          descriptionRequired: "वर्णन आवश्यक आहे",
          imageRequired: "किमान एक छायाचित्र आवश्यक आहे",
          nameRequired: "पूर्ण नाव आवश्यक आहे",
          phoneRequired: "फोन नंबर आवश्यक आहे",
          emailRequired: "ईमेल पत्ता आवश्यक आहे",
          emailInvalid: "कृपया वैध ईमेल पत्ता टाका",
        },
        errors: {
          photoFailed: "फोटो काढणे/निवडणे अयशस्वी",
          locationFailed: "सध्याचे स्थान मिळवण्यात अयशस्वी",
          validationFailed: "कृपया सत्यापन त्रुटी दुरुस्त करा",
          submitFailed: "अहवाल सादर करण्यात अयशस्वी. कृपया पुन्हा प्रयत्न करा.",
          maxImagesReached: "जास्तीत जास्त {{max}} छायाचित्रे अनुमतीत",
          tooManyImages:
            "तुम्ही फक्त {{remaining}} अधिक छायाचित्र(े) जोडू शकता. जास्तीत जास्त {{max}} छायाचित्रे अनुमतीत.",
        },
        success: {
          locationCaptured: "स्थान यशस्वीरित्या कॅप्चर केले",
          submitted: "अहवाल यशस्वीरित्या सादर केला!",
        },
        loading: {
          submitting: "आपला अहवाल सादर करत आहे...",
        },
        locationCaptured: "स्थान कॅप्चर केले",
        name: "नाव",
        namePlaceholder: "अहवालाचे नाव किंवा शीर्षक टाका",
        issueType: "समस्येचा प्रकार",
        issueTypePlaceholder: "समस्येचा प्रकार निवडा",
        description: "वर्णन",
        descriptionPlaceholder: "कृपया समस्येचे तपशीलवार वर्णन करा...",
        images: "छायाचित्रे",
        dragFiles: "फाइल्स येथे ड्रॅग करा किंवा अपलोड करण्यासाठी क्लिक करा",
        supportedFormats:
          "समर्थित: JPG, PNG, GIF (प्रत्येकी जास्तीत जास्त 5MB)",
        takePhoto: "फोटो घ्या",
        chooseFromGallery: "गॅलरीमधून निवडा",
        cancel: "रद्द करा",
        submitButton: "अहवाल सादर करा",
        submitting: "अहवाल सादर करत आहे...",
        submitSuccess: "अहवाल यशस्वीरित्या सादर केला!",
        submitError: "अहवाल सादर करण्यात अयशस्वी. कृपया पुन्हा प्रयत्न करा.",
        titleRequired: "अहवालाचे शीर्षक आवश्यक आहे",
        issueTypeRequired: "समस्येचा प्रकार आवश्यक आहे",
        descriptionRequired: "वर्णन आवश्यक आहे",
        photoError: "कॅमेरा/गॅलरी वापरण्यात त्रुटी",
        locationError: "आपले स्थान मिळवू शकले नाही",
      },
      issueTypes: {
        pothole: "खड्डा",
        streetlight: "रस्त्यावरील दिवा",
        drainage: "गटार व्यवस्था",
        waste: "कचरा व्यवस्थापन",
        traffic: "वाहतूक",
        accident: "वाहतूक अपघात",
        roadwork: "रस्ता बांधकाम/काम",
        flooding: "पूर",
        "illegal-dumping": "बेकायदेशीर कचरा टाकणे",
        damagedroad: "खराब रस्त्याची पृष्ठभाग",
        plumbing: "प्लंबिंग",
        electrical: "इलेक्ट्रिकल",
        hvac: "एचव्हीएसी/हीटिंग",
        security: "सुरक्षा",
        maintenance: "देखभाल",
        cleaning: "स्वच्छता",
        it: "आयटी/तंत्रज्ञान",
        other: "इतर",
      },
      login: {
        welcomeBack: "पुन्हा स्वागत आहे",
        signInToAccount: "आपल्या खात्यात साइन इन करा",
        emailOrMobile: "ईमेल किंवा मोबाइल नंबर",
        enterEmailOrMobile: "आपला ईमेल किंवा मोबाइल नंबर टाका",
        password: "पासवर्ड",
        enterPassword: "आपला पासवर्ड टाका",
        rememberMe: "मला लक्षात ठेवा",
        forgotPassword: {
          link: "पासवर्ड विसरलात?",
          sent: "पासवर्ड रीसेट लिंक आपल्या ईमेल किंवा मोबाइलवर पाठवली",
        },
        signIn: "साइन इन करा",
        signingIn: "साइन इन होत आहे...",
        noAccount: "खाते नाही?",
        signUp: {
          link: "साइन अप करा",
          redirect: "साइन अपकडे नेत आहे...",
        },
        orContinueWith: "किंवा यासह सुरू ठेवा",
        socialLogin: "सोशल लॉगिन",
        success: "लॉगिन यशस्वी! पुन्हा स्वागत आहे.",
        errors: {
          general: "लॉगिन अयशस्वी. कृपया पुन्हा प्रयत्न करा.",
          invalidCredentials:
            "चुकीचा ईमेल/मोबाइल किंवा पासवर्ड. कृपया आपली माहिती तपासा.",
          userNotFound: "या ईमेल किंवा मोबाइल नंबरसह कोणतेही खाते सापडले नाही.",
          accountLocked:
            "आपले खाते तात्पुरते लॉक केले आहे. कृपया नंतर प्रयत्न करा.",
          tooManyAttempts:
            "बरेच लॉगिन प्रयत्न. कृपया काही वेळानंतर प्रयत्न करा.",
          serverError: "सर्व्हर त्रुटी आली आहे. कृपया नंतर प्रयत्न करा.",
          networkError:
            "नेटवर्क कनेक्शन अयशस्वी. कृपया आपले इंटरनेट कनेक्शन तपासा.",
        },
        validation: {
          required: "ईमेल/मोबाइल आणि पासवर्ड आवश्यक आहे",
          invalidEmailOrMobile: "कृपया वैध ईमेल पत्ता किंवा मोबाइल नंबर टाका",
        },
      },
      signup: {
        title: "खाते तयार करा",
        subtitle: "आज आमच्यात सामील व्हा",
        firstName: "पहिले नाव",
        firstNamePlaceholder: "पहिले नाव",
        lastName: "आडनाव",
        lastNamePlaceholder: "आडनाव",
        emailOrMobile: "ईमेल किंवा मोबाइल नंबर",
        emailOrMobilePlaceholder: "आपला ईमेल किंवा मोबाइल नंबर टाका",
        password: "पासवर्ड",
        passwordPlaceholder: "पासवर्ड तयार करा",
        confirmPassword: "पासवर्डची पुष्टी करा",
        confirmPasswordPlaceholder: "आपल्या पासवर्डची पुष्टी करा",
        agreeToTerms: "मी मान्य करतो",
        termsOfService: "सेवा अटी",
        and: "आणि",
        privacyPolicy: "गोपनीयता धोरण",
        createAccount: "खाते तयार करा",
        creating: "खाते तयार करत आहे...",
        success: "खाते यशस्वीरित्या तयार केले!",
        error: "खाते तयार करण्यात अयशस्वी. कृपया पुन्हा प्रयत्न करा.",
        errors: {
          userExists: "या ईमेल किंवा मोबाइल नंबरसह खाते आधीच अस्तित्वात आहे",
          invalidData: "कृपया आपली माहिती तपासा आणि पुन्हा प्रयत्न करा",
          serverError: "सर्व्हर त्रुटी आली आहे. कृपया नंतर प्रयत्न करा",
          networkError:
            "नेटवर्क कनेक्शन अयशस्वी. कृपया आपले इंटरनेट कनेक्शन तपासा",
        },
        haveAccount: "आधीच खाते आहे?",
        signIn: "साइन इन करा",
        passwordStrength: {
          strength: "पासवर्ड मजबुती",
          weak: "कमकुवत",
          fair: "साधारण",
          good: "चांगला",
          strong: "मजबूत",
        },
        validation: {
          firstName: "पहिले नाव आवश्यक आहे",
          lastName: "आडनाव आवश्यक आहे",
          emailOrMobile: "कृपया वैध ईमेल पत्ता किंवा मोबाइल नंबर टाका",
          password: "पासवर्ड किमान 6 अक्षरांचा असावा",
          confirmPassword: "पासवर्ड जुळत नाही",
          terms: "कृपया अटी आणि शर्तींशी सहमत व्हा",
        },
      },
    },
  },
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    debug: process.env.NODE_ENV === "development",

    interpolation: {
      escapeValue: false,
    },

    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
    },
  });

export default i18n;
